<template>
  <div class="content-v2">
    <BreadcrumbV2 :items="breadcrumbItems" />
    <div class="business-detail">
      <div class="business-detail-img">
        <img :src="business.image" :alt="business.title" />
      </div>
      <div class="business-detail-info">
        <span>{{ business.deptName }}</span>
        <h2>{{ business.title }}</h2>
        <div class="business-detail-content" v-html="formattedContent"></div>
      </div>
    </div>
  </div>
</template>

<script>
import BreadcrumbV2 from '@/components/BreadcrumbV2.vue'
import { getBusinessDetail, getBusinessList } from '@/api/portal'
import { resolveFileUrl } from '@/utils/request'
import imageA from '@/assets/images/banner2.svg'
import imageB from '@/assets/images/case1.svg'
import imageC from '@/assets/images/case2.svg'
import imageD from '@/assets/images/banner3.svg'
import imageE from '@/assets/images/banner4.svg'
import imageF from '@/assets/images/banner5.svg'

const fallbackBusiness = [
  {
    id: 'debug',
    deptName: '调试',
    title: '电力调试服务',
    content: '面向火电、新能源、生物质、垃圾发电、燃机等项目，提供分系统调试、整套启动调试及并网前后技术支持。',
    image: imageA
  },
  {
    id: 'maintain',
    deptName: '维护',
    title: '设备维护与运行保障',
    content: '围绕设备状态、运行数据和现场缺陷，开展计划维护、巡检消缺、技术监督和稳定运行保障。',
    image: imageB
  },
  {
    id: 'overhaul',
    deptName: '检修',
    title: '机组检修与专项治理',
    content: '承接火电、新能源及综合能源项目检修任务，覆盖设备检修、系统治理、隐患排查和质量验收。',
    image: imageC
  },
  {
    id: 'test',
    deptName: '电力试验',
    title: '性能试验与设备检测',
    content: '提供性能试验、热力试验、保护定值计算、设备健康检测等技术服务，为项目安全经济运行提供依据。',
    image: imageD
  },
  {
    id: 'operation',
    deptName: '运行',
    title: '电厂托管与运行服务',
    content: '面向电厂托管运营和项目运行需求，提供运行组织、制度建设、岗位培训和现场管理支持。',
    image: imageE
  },
  {
    id: 'lab',
    deptName: '油化实验室',
    title: '油化检测与实验分析',
    content: '围绕绝缘油、汽水品质、化学监督等方向开展检测分析，支撑设备健康评价和运行风险控制。',
    image: imageF
  }
]

export default {
  name: 'AboutBusinessDetailV2',
  components: { BreadcrumbV2 },
  data() {
    return {
      business: fallbackBusiness[0]
    }
  },
  computed: {
    breadcrumbItems() {
      return [
        { nameKey: 'common.about', path: '/about' },
        { nameKey: 'common.business', path: '/about/business' }
      ]
    },
    formattedContent() {
      if (!this.business.content) {
        return ''
      }
      if (/<[a-z][\s\S]*>/i.test(this.business.content)) {
        return this.business.content
      }
      return this.business.content
        .split(/\n+/)
        .filter(Boolean)
        .map(text => `<p>${text}</p>`)
        .join('')
    }
  },
  watch: {
    '$route.params.id': {
      immediate: true,
      handler() {
        this.loadDetail()
      }
    }
  },
  methods: {
    loadDetail() {
      const id = this.$route.params.id
      const fallback = fallbackBusiness.find(item => String(item.id) === String(id)) || fallbackBusiness[0]
      this.business = fallback
      if (/^\d+$/.test(String(id))) {
        getBusinessDetail(id).then(data => {
          if (!data || !data.businessId) {
            return
          }
          this.business = {
            id: data.businessId,
            deptName: data.deptName,
            title: data.introTitle,
            content: data.introContent,
            image: resolveFileUrl(data.imageUrl, fallback.image)
          }
        }).catch(() => {})
      } else {
        getBusinessList().then(list => {
          const index = list.findIndex(item => String(item.businessId) === String(id))
          if (index < 0) {
            return
          }
          const data = list[index]
          this.business = {
            id: data.businessId,
            deptName: data.deptName,
            title: data.introTitle,
            content: data.introContent,
            image: resolveFileUrl(data.imageUrl, fallbackBusiness[index % fallbackBusiness.length].image)
          }
        }).catch(() => {})
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.business-detail {
  display: grid;
  grid-template-columns: 440px minmax(0, 1fr);
  gap: 44px;
  align-items: start;
  background: #fff;
  border: 1px solid #dbe7f5;
  padding: 42px;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.06);
}

.business-detail-img {
  background: #e2e8f0;
  overflow: hidden;

  img {
    width: 100%;
    aspect-ratio: 4 / 3;
    display: block;
    object-fit: cover;
  }
}

.business-detail-info {
  span {
    display: inline-flex;
    align-items: center;
    height: 30px;
    padding: 0 14px;
    background: #eff6ff;
    color: #1d4ed8;
    font-size: 14px;
    font-weight: 700;
  }

  h2 {
    margin: 18px 0 22px;
    color: #0f172a;
    font-size: 32px;
    line-height: 1.35;
  }
}

.business-detail-content {
  font-size: 17px;
  line-height: 2;
  color: #475569;

  ::v-deep p {
    margin-bottom: 16px;
    text-indent: 2em;
  }
}

@media (max-width: 960px) {
  .business-detail {
    grid-template-columns: 1fr;
    padding: 24px;
  }
}
</style>
